import {Device} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
import {useTrackOverlay} from '@aboutyou/tracking';
import {PushNotificationFlyout} from '@aboutyou/ui-components';
import {useUserTokenOrSessionId} from '@aboutyou/user';
import React, {useEffect, useRef} from 'react';
import {SessionCountProvider} from '../../../client-services/SessionCountProvider/SessionCountProvider';
import {useBrowserSpecificUUID} from '../../../features/BrowserSpecificUUID/useBrowserSpecificUUID';
import {DevicePushState} from '../../../features/useDevicePushState/enums/DevicePushState';
import {useDevicePushState} from '../../../features/useDevicePushState/useDevicePushState';
import {getUserPermissionsAndFirebaseToken} from '../getUserPermissionsAndFirebaseToken';
import {useNotifyBackendInCaseNotHappened} from '../hooks/useNotifyBackendInCaseNotHappened';
import {usePushNotification} from '../usePushNotification';
import {useShouldShowLongSessionTrigger} from './hooks/useShouldShowLongSessionTrigger';

export const PushNotificationLongSessionTrigger = () => {
  return (
    <SessionCountProvider>
      <Inner />
    </SessionCountProvider>
  );
};

const Inner = () => {
  const {shouldShow, onClose} = useShouldShowLongSessionTrigger();
  return (
    <PushNotificationLongSessionMemo
      onClose={onClose}
      shouldShow={shouldShow}
    />
  );
};

const PushNotificationLongSession: React.FC<{
  shouldShow: boolean;
  onClose: VoidFunction;
}> = ({shouldShow, onClose}) => {
  const {updateDevicePushState} = useDevicePushState();
  const {updatePushNotification} = usePushNotification();
  const uuid = useBrowserSpecificUUID();
  const trackOverlay = useTrackOverlay();
  const handleTrackOverlay = (action: 'impression' | 'click' | 'submit') => {
    trackOverlay({
      action,
      content_type: 'bottom_sheet',
      name: 'push_notification_flyout',
    });
  };
  const impressionTrackedRef = useRef(false);
  const {data: tokenOrSessionId} = useUserTokenOrSessionId();

  const updatePushNotificationByCase = (
    newState: DevicePushState,
    token?: string,
  ) => {
    if (
      newState === DevicePushState.SENT ||
      newState === DevicePushState.BLOCKED
    ) {
      updatePushNotification({
        device: Device.MOBILE_WEB,
        token: token || '',
        devicePushState: token ? 'granted' : 'denied',
        uuid,
        session: tokenOrSessionId,
      });
    }
  };

  useNotifyBackendInCaseNotHappened(updatePushNotificationByCase);

  const handleClose = () => {
    handleTrackOverlay('click');
    onClose();

    const newState = updateDevicePushState();
    updatePushNotificationByCase(newState);
  };

  const handleBodyContentClick = () => {
    onClose();
    handleTrackOverlay('submit');
    getUserPermissionsAndFirebaseToken().then(token => {
      const newState = updateDevicePushState();
      updatePushNotificationByCase(newState, token);
    });
  };

  useEffect(() => {
    if (shouldShow && !impressionTrackedRef.current) {
      handleTrackOverlay('impression');
      impressionTrackedRef.current = true;
    }
  }, [shouldShow]);

  return (
    <FlyoutWrapper shouldShow={shouldShow}>
      <PushNotificationFlyout
        open={shouldShow}
        onClose={handleClose}
        onBodyContentClick={handleBodyContentClick}
        type="long_session"
      />
    </FlyoutWrapper>
  );
};

const PushNotificationLongSessionMemo = React.memo(PushNotificationLongSession);

export const ANIMATION_DELAY = 2000;

const FlyoutWrapper = (
  props: React.PropsWithChildren<{
    shouldShow: boolean;
  }>,
) => {
  const {shouldShow} = props;
  const [timedOut, setTimedOut] = React.useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (shouldShow) {
      timeout = setTimeout(() => {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setTimedOut(true);
      }, ANIMATION_DELAY);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [shouldShow]);

  if (!timedOut) return null;

  return <>{props.children}</>;
};
